import { type PinyinFinal, getPinyinFinal } from "#pinyin-final";
import { type PinyinInitial, getPinyinInitial } from "#pinyin-initial";
import { PINYIN_TONE_NAMES, type PinyinTone, getPinyinTone } from "#pinyin-tone";

/** One of the parts a syllable is built from: its initial, its final or its tone. */
export type PinyinSound =
  | { kind: "final"; sound: PinyinFinal }
  | { kind: "initial"; sound: PinyinInitial }
  | { kind: "tone"; sound: PinyinTone };

/**
 * The sounds a tone-number syllable is built from, in reading order: its initial, its final and its
 * tone (`xiong2` → `x-`, `-iong`, rising). A part the charts have no cell for is left out: the
 * missing initial of a lone final (`an4`), the buzzed `-i` of 是 shì, and the tone of a syllable
 * without a digit.
 */
export function getPinyinSounds(toneNumbersPinyin: string): PinyinSound[] {
  const sounds: PinyinSound[] = [];
  const initial = getPinyinInitial(toneNumbersPinyin);
  if (initial !== null) {
    sounds.push({ kind: "initial", sound: initial });
  }
  const final = getPinyinFinal(toneNumbersPinyin);
  if (final !== null) {
    sounds.push({ kind: "final", sound: final });
  }
  const tone = getPinyinTone(toneNumbersPinyin);
  if (tone !== null) {
    sounds.push({ kind: "tone", sound: tone });
  }
  return sounds;
}

/**
 * How a chart writes a sound: an initial with a trailing hyphen (`b-`, `zh-`), a final with a
 * leading one (`-ia`, `-ong`), marking which end of the syllable it spells, and a tone by its name
 * (`Falling`).
 */
export function formatPinyinSoundLabel(sound: PinyinSound): string {
  switch (sound.kind) {
    case "initial": {
      return `${sound.sound}-`;
    }
    case "final": {
      return `-${sound.sound}`;
    }
    case "tone": {
      return PINYIN_TONE_NAMES[sound.sound];
    }
  }
}

/** How a sentence names a sound: `Initial b-`, `Final -ong`, `Falling tone`. */
export function formatPinyinSoundName(sound: PinyinSound): string {
  const label = formatPinyinSoundLabel(sound);
  switch (sound.kind) {
    case "initial": {
      return `Initial ${label}`;
    }
    case "final": {
      return `Final ${label}`;
    }
    case "tone": {
      return `${label} tone`;
    }
  }
}

/** The initials, finals and tones a learner has learned. */
export type LearnedPinyinSounds = {
  finals: ReadonlySet<PinyinFinal>;
  initials: ReadonlySet<PinyinInitial>;
  tones: ReadonlySet<PinyinTone>;
};

function addPinyinSounds(
  sounds: { finals: Set<PinyinFinal>; initials: Set<PinyinInitial>; tones: Set<PinyinTone> },
  toneNumbersPinyinList: Iterable<string>,
): void {
  for (const toneNumbersPinyin of toneNumbersPinyinList) {
    for (const sound of getPinyinSounds(toneNumbersPinyin)) {
      switch (sound.kind) {
        case "initial": {
          sounds.initials.add(sound.sound);
          break;
        }
        case "final": {
          sounds.finals.add(sound.sound);
          break;
        }
        case "tone": {
          sounds.tones.add(sound.sound);
          break;
        }
      }
    }
  }
}

/**
 * Every sound the given tone-number readings are built from: a learner who has learned a reading
 * has learned its initial, final and tone.
 */
export function collectLearnedPinyinSounds(
  toneNumbersPinyinList: Iterable<string>,
): LearnedPinyinSounds {
  const sounds = {
    finals: new Set<PinyinFinal>(),
    initials: new Set<PinyinInitial>(),
    tones: new Set<PinyinTone>(),
  };
  addPinyinSounds(sounds, toneNumbersPinyinList);
  return sounds;
}

/** `learned` together with every sound of `toneNumbersPinyinList`, which a card is teaching. */
export function withLearnedPinyin(
  learned: LearnedPinyinSounds,
  toneNumbersPinyinList: Iterable<string>,
): LearnedPinyinSounds {
  const sounds = {
    finals: new Set(learned.finals),
    initials: new Set(learned.initials),
    tones: new Set(learned.tones),
  };
  addPinyinSounds(sounds, toneNumbersPinyinList);
  return sounds;
}

export function isPinyinSoundLearned(sound: PinyinSound, learned: LearnedPinyinSounds): boolean {
  switch (sound.kind) {
    case "initial": {
      return learned.initials.has(sound.sound);
    }
    case "final": {
      return learned.finals.has(sound.sound);
    }
    case "tone": {
      return learned.tones.has(sound.sound);
    }
  }
}

/** Whether a learner who knows `learned` can read a syllable: every sound it is built from. */
export function isPinyinLearned(toneNumbersPinyin: string, learned: LearnedPinyinSounds): boolean {
  return getPinyinSounds(toneNumbersPinyin).every((sound) => isPinyinSoundLearned(sound, learned));
}
